import { useState } from 'react'
import type { BuilderBlock, ShopifyVariationFeedbackVote } from '@testfeed/shopify-contracts'
import { ShopifyPolarisModal } from '../StudySetupDeleteDialog'
import type { Locks, OnEdit, PendingValues } from './SectionBlocks'

type FeedbackBlock = Extract<BuilderBlock, { kind: 'suggestion-feedback' }>
type Vote = ShopifyVariationFeedbackVote

const VOTES: Vote[] = ['up', 'down']
const ICONS: Record<Vote, 'thumbs-up' | 'thumbs-down'> = {
  up: 'thumbs-up',
  down: 'thumbs-down',
}

export function SuggestionFeedbackBlock({
  block,
  onEdit,
  pending,
  locks,
}: {
  block: FeedbackBlock
  onEdit: OnEdit
  pending: PendingValues
  locks: Locks
}) {
  return (
    <SuggestionFeedbackForm
      key={`${block.key}:${JSON.stringify([block.savedVote, block.savedReasonCodes, block.savedComment])}`}
      block={block}
      onEdit={onEdit}
      pending={pending.has(block.key)}
      locked={locks.structure}
    />
  )
}

function SuggestionFeedbackForm({
  block,
  onEdit,
  pending,
  locked,
}: {
  block: FeedbackBlock
  onEdit: OnEdit
  pending: boolean
  locked: boolean
}) {
  const currentSavedVote: Vote | null = block.savedVote === 'up' || block.savedVote === 'down' ? block.savedVote : null
  const [vote, setVote] = useState<Vote | null>(currentSavedVote)
  const [reasons, setReasons] = useState<string[]>(block.savedReasonCodes)
  const [comment, setComment] = useState(block.savedComment ?? '')
  const [open, setOpen] = useState(false)
  const disabled = pending || locked || block.disabled

  const beginFeedback = (selectedVote: Vote) => {
    setVote(selectedVote)
    setReasons([])
    setComment('')
    setOpen(true)
  }
  const cancel = () => {
    setVote(currentSavedVote)
    setReasons(block.savedReasonCodes)
    setComment(block.savedComment ?? '')
    setOpen(false)
  }
  const submit = () => {
    if (!vote || disabled || (vote === 'up' && reasons.length === 0)) return
    onEdit(block.key, new URLSearchParams({ vote, reasonCodes: reasons.join(','), comment }).toString())
  }
  const chooseReason = (code: string, checked: boolean) => {
    setReasons((current) => {
      if (checked && current.length >= 3) return current
      return checked ? [...current, code] : current.filter((reason) => reason !== code)
    })
  }
  const chooseVote = (selectedVote: Vote) => {
    setVote(selectedVote)
    if (selectedVote === 'up') {
      setReasons([])
      setComment('')
    }
  }

  return (
    <>
      {block.savedVote ? (
        <s-stack direction="inline" gap="small-100">
          {currentSavedVote ? (
            <s-button
              variant="tertiary"
              icon={ICONS[currentSavedVote]}
              accessibilityLabel={block.labels[currentSavedVote]}
              disabled
            />
          ) : null}
          <s-text color="subdued">{block.labels.saved}</s-text>
        </s-stack>
      ) : (
        <s-stack direction="inline" gap="small-100">
          {VOTES.map((option) => (
            <s-button
              key={option}
              variant="tertiary"
              icon={ICONS[option]}
              accessibilityLabel={block.labels[option]}
              disabled={disabled}
              onClick={() => beginFeedback(option)}
            />
          ))}
        </s-stack>
      )}
      <ShopifyPolarisModal
        open={open && !block.savedVote}
        onClose={cancel}
        title={block.labels.title}
        size="small"
        primaryAction={{
          content: block.labels.submit,
          disabled: !vote || disabled || (vote === 'up' && reasons.length === 0),
          loading: pending,
          onAction: submit,
        }}
        secondaryActions={[{ content: block.labels.cancel, onAction: cancel, disabled: pending }]}
      >
        <s-stack direction="block" gap="base">
          <s-text color="subdued">{block.labels.responsePrompt}</s-text>
          <s-stack direction="inline" gap="small-100">
            {VOTES.map((option) => (
              <s-button
                key={option}
                variant={vote === option ? 'primary' : 'tertiary'}
                icon={ICONS[option]}
                accessibilityLabel={block.labels[option]}
                disabled={disabled}
                onClick={() => chooseVote(option)}
              />
            ))}
          </s-stack>
          {vote ? (
            <s-stack direction="block" gap="small-200">
              <s-text color="subdued">
                {vote === 'up' ? block.labels.positiveReasonsPrompt : block.labels.negativeReasonsPrompt}
              </s-text>
              {(vote === 'up' ? block.labels.positiveReasons : block.labels.negativeReasons).map((reason) => (
                <s-checkbox
                  key={reason.code}
                  label={reason.label}
                  checked={reasons.includes(reason.code)}
                  disabled={disabled || (!reasons.includes(reason.code) && reasons.length >= 3)}
                  onInput={(event) => chooseReason(reason.code, event.currentTarget.checked)}
                />
              ))}
              {reasons.length === 3 ? <s-text color="subdued">{block.labels.reasonLimit}</s-text> : null}
              <s-text-area
                label={block.labels.commentPrompt}
                value={comment}
                rows={2}
                maxLength={500}
                disabled={disabled}
                onInput={(event) => setComment(event.currentTarget.value)}
              />
            </s-stack>
          ) : null}
        </s-stack>
      </ShopifyPolarisModal>
    </>
  )
}
